// Stack — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as slidingWindow.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import stackDrills from './stackDrills';
const drill = id => stackDrills.find(d => d.id === id);

const stack = {
  key: 'Stack',
  tagline: 'Last in, first out — a structure that only ever lets you touch the most recent thing you added.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Stacks',
      type: 'text',
      body: [
        "A stack is a collection with one strict rule: you can only add to, or remove from, ONE end — called the top. Whatever was added most recently is always the first thing that comes back off. This is called LIFO — Last In, First Out.",
        "That single restriction is what makes a stack useful. An array lets you touch any index; a stack deliberately gives that up in exchange for a guarantee: whatever you're looking at right now is always the most recently seen, still-unresolved thing. That guarantee is exactly what a huge number of problems — matching brackets, undoing actions, evaluating expressions, finding the next bigger value — turn out to need.",
      ],
    },
    {
      id: 'why',
      title: 'Why Stacks?',
      type: 'text',
      body: [
        "Plenty of problems have a hidden shape: \"something opens, and later, something else has to close it — in the exact reverse order they opened.\" Nested brackets, nested function calls, nested encoded strings, undo/redo — all of them share this shape.",
        "You could try to track \"what's still open\" with a plain variable or two, but the moment there's more than one thing open at once (a bracket inside a bracket, a function calling a function), a single variable can't hold enough state. A stack can hold arbitrarily many \"still open\" items, and — critically — always hands you back the most recently opened one first, which is exactly the order they need to be closed in.",
        "A second, less obvious use shows up constantly in interviews: a monotonic stack (one kept always increasing or always decreasing from bottom to top) lets you answer \"what's the next bigger/smaller value to the right?\" for every element in a single O(n) pass, instead of an O(n²) nested scan.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Stacks Work — Two Real-World Pictures',
      type: 'text',
      heading: 'The Undo Button, and a Row of Buildings Along a Road',
      body: [
        "Picture a text editor's undo history. Every action you take — typing a word, deleting a line, pasting a paragraph — gets recorded, one on top of the last. Press Ctrl+Z, and you don't undo a random action from an hour ago; you undo the MOST RECENT one, always. If that undo itself becomes undo-able (redo), it goes back on top of the same history. You never get to reach into the middle of your undo history and pull out an arbitrary action — you can only ever touch the most recent one. That's a stack, exactly: push on every new action, pop on every undo.",
        "Now picture driving down a road lined with buildings of different heights, looking for the tallest one visible from each point. As you pass a building shorter than the one right behind it, that shorter building becomes permanently hidden — the taller one blocks it forever, no matter how much farther you drive. But if you pass a building TALLER than the ones just behind it, those shorter ones are still hidden by whatever's now the tallest-so-far. This is the intuition behind a monotonic stack: as you scan through data left to right, you keep only the values that could still possibly matter (the ones \"tall enough\" to still be visible), discarding — resolving — anything a bigger value has just permanently blocked from view.",
      ],
    },
    {
      id: 'internals',
      title: 'Visualizing Push and Pop',
      type: 'stack-diagram',
      body: [
        "A stack only ever exposes its top — everything below is temporarily invisible until the items above it are removed. The diagram below shows a stack after three pushes: 4, then 9, then 2. Calling pop() right now returns 2 (the top) and nothing else — 9 and 4 stay exactly where they are, still buried, until popped in turn.",
        "This is the entire mental model: push adds a new top, pop removes and returns the current top, and peek looks at the current top without removing it. Every stack algorithm is built from just these three moves.",
      ],
      demo: {
        values: [4, 9, 2],
        note: 'push(4) → push(9) → push(2). Calling pop() now returns 2 — the most recently pushed value, and the only one currently reachable.',
      },
    },
    {
      id: 'declare',
      title: 'Declaring a Stack in Java',
      type: 'code',
      code:
`// Preferred: ArrayDeque used as a stack
Deque<Integer> stack = new ArrayDeque<>();

// Legacy class — works, but synchronized (slower) and technically deprecated in spirit
Stack<Integer> oldStack = new Stack<>();`,
      note: 'ArrayDeque is the modern, recommended choice — java.util.Stack still exists (and still compiles fine) but carries synchronization overhead from Java\'s early Vector-based design that almost no single-threaded program needs.',
    },
    {
      id: 'operations',
      title: 'Core Operations: push, pop, peek, isEmpty',
      type: 'code',
      code:
`Deque<Integer> stack = new ArrayDeque<>();

stack.push(10);           // 10 is now the top
stack.push(20);           // 20 is now the top, 10 is buried beneath it

int top = stack.peek();   // 20 — look, don't remove
int popped = stack.pop(); // 20 — remove and return; 10 is the top again

boolean empty = stack.isEmpty();  // false — 10 is still there`,
      note: 'Calling pop() or peek() on an empty stack throws NoSuchElementException with ArrayDeque — always check isEmpty() first if the stack might be empty.',
    },
    {
      id: 'callstack',
      title: 'The Call Stack — Recursion Is a Stack You Don\'t Manage Yourself',
      type: 'text',
      body: [
        "Every time a Java method calls another method, the runtime pushes a new frame onto the call stack — holding that method's local variables and where to resume once it returns. When a method returns, its frame is popped, and control resumes exactly where the caller left off. This is a real stack, managed automatically, and it's why recursive functions work at all: each recursive call is a push, each return is a pop.",
        "It's also why deep recursion can fail with a StackOverflowError — the call stack has a finite size, and enough nested pushes without matching pops (or simply too many levels of legitimate nesting) exhausts it. This is precisely the trade-off in the \"Decode a Nested Encoded String\" drill below: the recursive version is elegant but relies on Java's own call stack, while the iterative version trades that elegance for two explicit stacks that can't overflow the same way.",
      ],
    },
    {
      id: 'monotonic',
      title: 'The Monotonic Stack Pattern',
      type: 'code',
      code:
`int[] result = new int[arr.length];
Arrays.fill(result, -1);
Deque<Integer> stack = new ArrayDeque<>();  // indices; values strictly decreasing bottom to top

for (int i = 0; i < arr.length; i++) {
    while (!stack.isEmpty() && arr[stack.peek()] < arr[i]) {
        result[stack.pop()] = arr[i];   // arr[i] is the "next greater" for whatever was popped
    }
    stack.push(i);
}
// anything still on the stack when the loop ends has no next greater element`,
      note: 'The while loop looks like it could make this O(n²), but it isn\'t: every index is pushed exactly once and popped at most once across the ENTIRE run, so the total work across all iterations is still O(n) — this is the single most important stack pattern for interviews.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Calling pop() or peek() without checking isEmpty() first',
          why: 'ArrayDeque throws NoSuchElementException immediately if the stack has nothing to give — a very common crash in bracket-matching code when a closing bracket appears with nothing open to match it.',
          fix: 'Always check stack.isEmpty() before pop() or peek() whenever the stack might legitimately be empty at that point.',
        },
        {
          mistake: 'Using Queue-style thinking (FIFO) by accident',
          why: 'It\'s an easy slip to reach for the first element added instead of the most recent one — a stack and a queue share a similar interface shape but return elements in exactly opposite order.',
          fix: 'Remember: stack = LIFO (top, most recent). Queue = FIFO (front, oldest). If the problem cares about "the most recently opened thing," it wants a stack.',
        },
        {
          mistake: 'Popping two operands off a stack in the wrong order for non-commutative operators',
          why: 'For subtraction or division in postfix evaluation, popping gives you the SECOND operand first — writing a - b (using pop order directly) computes b - a instead, silently flipping every non-commutative result.',
          fix: 'Explicitly pop into two named variables (e.g. int b = stack.pop(); int a = stack.pop();) and apply the operator as a - b, not the reverse.',
        },
        {
          mistake: 'Using java.util.Stack by habit and accepting its synchronization cost',
          why: 'java.util.Stack extends the old, synchronized Vector class — every operation pays a locking cost that\'s pure overhead in ordinary single-threaded code.',
          fix: 'Default to Deque<T> stack = new ArrayDeque<>() instead — same LIFO behavior, no synchronization tax.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'push / pop / peek / isEmpty', time: 'O(1)', note: 'Every operation only ever touches the top — no shifting, no searching.' },
        { op: 'Monotonic stack, full array scan', time: 'O(n)', note: 'Looks like a nested loop, but each index is pushed once and popped at most once total.' },
        { op: 'Search for an arbitrary value', time: 'O(n)', note: 'A stack gives up random access on purpose — finding anything but the top requires popping down to it.' },
        { op: 'Space, in every variant above', time: 'O(n)', note: 'At most every element is on the stack at once.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for a stack when you see…',
      body: [
        'Nested or paired structures that must close in the exact reverse order they opened — brackets, tags, nested function calls.',
        'A need to undo the most recent action, specifically — not the oldest one.',
        'A request for the "next greater" or "next smaller" element (in either direction) for every position in an array — the monotonic stack pattern.',
        'Evaluating an expression (postfix, or converting infix to postfix) where operators need their most-recently-seen operands.',
        'Avoid it when order doesn\'t matter, or when you specifically need FIFO (oldest-first) behavior — that\'s a queue, not a stack.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('valid-parentheses'),
            drill('reverse-string-using-stack'),
            drill('implement-stack-using-array'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('next-greater-element'),
            drill('min-stack-design'),
            drill('evaluate-postfix'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('daily-temperatures'),
            drill('largest-rectangle-histogram'),
            drill('decode-string'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with both plain stack mechanics and the monotonic stack pattern before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'LIFO: last in, first out. Only the top is ever reachable — push, pop, and peek are all O(1).',
        'Prefer Deque<T> stack = new ArrayDeque<>() over the legacy, synchronized java.util.Stack.',
        'The signal for a plain stack: nested structures that must close in exactly reverse order — brackets, tags, undo history, recursive calls.',
        'The monotonic stack pattern answers "next greater/smaller element" for an entire array in O(n) — every index is pushed once, popped at most once.',
        'The call stack IS a stack — deep recursion risks StackOverflowError; an iterative version with explicit stacks avoids that ceiling.',
        'For non-commutative operators (subtraction, division) in postfix evaluation, pop order matters: the first pop is the SECOND operand.',
        'A stack deliberately gives up random access — if you need to reach an arbitrary element without unwinding everything above it, a stack is the wrong structure.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default stack;
